import { useWidgetData, aggregate, currencyFmt } from './shared';
import type {
  TableSummaryDashboardWidget,
  TableSummaryConfig,
  WidgetAggregation,
} from '../../types/panelConfig';

interface TableSummaryWidgetProps {
  widget: TableSummaryDashboardWidget;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

type Row = Record<string, unknown>;

function collapse(rows: Row[], cfg: TableSummaryConfig): Row[] {
  if (!cfg.groupBy) return rows.slice();
  const groupBy = cfg.groupBy;
  const buckets = new Map<string, Row[]>();
  for (const row of rows) {
    const key = String(row[groupBy] ?? '');
    if (!key) continue;
    const existing = buckets.get(key);
    if (existing) existing.push(row);
    else buckets.set(key, [row]);
  }
  return Array.from(buckets.entries()).map(([key, group]) => {
    const out: Row = { [groupBy]: key };
    for (const col of cfg.columns) {
      if (col.field === groupBy) continue;
      const method: WidgetAggregation = col.agg ?? 'sum';
      out[col.field] = aggregate(group, col.field, method);
    }
    return out;
  });
}

function formatCell(value: unknown, type?: string): string {
  if (value === null || value === undefined) return '—';
  if (type === 'currency') return currencyFmt.format(Number(value) || 0);
  if (type === 'number') return new Intl.NumberFormat('tr-TR').format(Number(value) || 0);
  return String(value);
}

export function TableSummaryWidget({ widget, sourceId, parameters }: TableSummaryWidgetProps) {
  const { data, isLoading } = useWidgetData(sourceId, `table-${widget.reportCode}`, parameters);
  const cfg = widget.config;
  if (!cfg) {
    return <div data-testid="table-summary-widget" className="text-subtle text-sm p-2">Yapılandırma hatası</div>;
  }

  let rows = collapse(data ?? [], cfg);
  if (cfg.sortBy) {
    const dir = cfg.sortDir === 'asc' ? 1 : -1;
    const sortBy = cfg.sortBy;
    rows = rows.slice().sort((a, b) => (Number(a[sortBy]) - Number(b[sortBy])) * dir);
  }
  if (cfg.topN) rows = rows.slice(0, cfg.topN);

  if (isLoading) {
    return (
      <div data-testid="table-summary-widget" className="text-subtle text-sm p-2">
        Yükleniyor…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div data-testid="table-summary-widget" className="text-subtle text-sm p-2">
        Veri yok
      </div>
    );
  }

  return (
    <div data-testid="table-summary-widget" className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-subtle text-xs uppercase">
            {cfg.columns.map((c) => (
              <th key={c.field} className={`pb-2 ${c.type ? 'text-right' : 'text-left'}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-t border-border">
              {cfg.columns.map((c) => (
                <td
                  key={c.field}
                  className={`py-2 ${c.type ? 'text-right tabular-nums' : 'text-left'}`}
                >
                  {formatCell(row[c.field], c.type)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
